import type { LogEntry } from "../API/Models";

/**
 * Raw payload pushed on the SignalR "activity" channel — one per `SystemLogService` write on the
 * server (see CMS.Core SystemLogService). Shapes onto {@link LogEntry} so the live feed and the
 * polled `/api/logs` list share one rendering path on the dashboard.
 */
export type ActivityPayload = {
	id?: string | null;
	type?: string | null;
	level?: string | null;
	message?: string | null;
	details?: string | null;
	user?: string | null;
	tags?: string[] | null;
	createdAt?: string | null;
};

/** Map a live activity payload to the LogEntry shape the feed renders. */
export const activityToLogEntry = (payload: ActivityPayload): LogEntry => ({
	id: payload.id ?? undefined,
	type: payload.type ?? "",
	level: payload.level ?? "Information",
	message: payload.message ?? "",
	details: payload.details ?? "",
	user: payload.user ?? "SYSTEM",
	tags: payload.tags ?? [],
	createdAt: payload.createdAt ?? undefined
});

/** Stable identity for a log entry — its id, or a content signature when the id is missing. */
const entryKey = (entry: LogEntry): string =>
	entry.id && entry.id.length > 0 ? entry.id : `${entry.createdAt ?? ""}|${entry.message ?? ""}`;

/**
 * Merge one freshly-streamed entry into the existing feed: prepend it (newest first), drop any
 * duplicate already present (by id/signature), and cap the list length so the feed can stream
 * indefinitely without growing unbounded. Pure — safe to unit test and to call from a setState
 * updater.
 */
export const mergeActivityEntry = (existing: LogEntry[], incoming: LogEntry, cap: number): LogEntry[] => {
	const key = entryKey(incoming);
	const deduped = existing.filter((entry) => entryKey(entry) !== key);
	const next = [incoming, ...deduped];
	return cap > 0 && next.length > cap ? next.slice(0, cap) : next;
};

/**
 * A run of consecutive identical events collapsed into one display row. `count` is how many were
 * folded together; `latestAt`/`earliestAt` bracket the run (the feed is newest-first, so `latestAt`
 * is the most recent occurrence and `earliestAt` the oldest in the run).
 */
export type ActivityGroup = LogEntry & { count: number; latestAt?: string; earliestAt?: string };

/**
 * Whether an event is machine-generated (the recurring ForexAPI sync, feed auto-publish, or any
 * SYSTEM/unattributed write) rather than a human action. Automated noise is collapsed aggressively
 * and rendered demoted; human actions are kept distinct and emphasised.
 */
export const isAutomated = (entry: LogEntry): boolean => {
	const user = (entry.user ?? "").trim().toUpperCase();
	const type = (entry.type ?? "").toLowerCase();
	return user === "" || user === "SYSTEM" || type === "forex-sync" || type === "content-auto-publish";
};

/**
 * Signature that makes two events "the same kind of thing" for collapsing. Automated events fold on
 * type + actor alone, so a run of FX syncs collapses even though their message drifts ("21 rates"→
 * "20 rates"); human events still require an identical message so genuinely different actions stay apart.
 */
const groupSignature = (entry: LogEntry): string =>
	isAutomated(entry)
		? `auto|${(entry.type ?? "").toLowerCase()}|${(entry.user ?? "").trim().toLowerCase()}`
		: `${entry.type ?? ""}|${(entry.message ?? "").trim().toLowerCase()}|${(entry.user ?? "").trim().toLowerCase()}`;

/**
 * Collapse runs of consecutive identical events (e.g. the recurring "ForexAPI sync …" auto-job) into
 * a single row carrying a count and the run's time span. Only *adjacent* matches fold together, so the
 * feed's chronology is preserved and a human action that interleaves naturally breaks the run. Pure.
 */
export const groupActivity = (entries: LogEntry[]): ActivityGroup[] => {
	const groups: ActivityGroup[] = [];
	for (const entry of entries) {
		const last = groups[groups.length - 1];
		if (last && groupSignature(last) === groupSignature(entry)) {
			last.count += 1;
			if (entry.createdAt) last.earliestAt = entry.createdAt;
		} else {
			groups.push({ ...entry, count: 1, latestAt: entry.createdAt, earliestAt: entry.createdAt });
		}
	}
	return groups;
};
